import { ValuesType } from 'utility-types'

export enum CATALOG_CATEGORY {
  ALL = 'all',
  BUILD = 'build',
  CI_CD = 'ci-cd',
  COMPUTE = 'compute',
  CUSTOM = 'custom',
  BETA = 'beta',
  HR = 'hr',
  IDENTITY_MANAGEMENT = 'identity-management',
  ORG = 'org',
  MOCK = 'mock',
  MONITORING = 'monitoring',
  QA = 'qa',
  REGISTRIES = 'registries',
  SECURITY = 'security',
  SOURCE_CODE = 'source-code',
  STORAGE = 'storage',
  SURVEY = 'survey',
  TICKET_MANAGEMENT = 'ticket-management',
  CUSTOM_EVENTS = 'custom-events',
  TRANSFORMS = 'transforms',
  EXPORT = 'export',
}

export enum JSON_SCHEMA_TYPE {
  STRING = 'string',
  NUMBER = 'number',
  OBJECT = 'object',
  ARRAY = 'array',
  BOOLEAN = 'boolean',
  NULL = 'null',
  // not valid json schema type
  FIELD_SELECT = 'fieldSelect',
  SELECT = 'select',
  CONNECTION_SELECT = 'connectionSelect',
  ENHANCED_SELECT = 'enhancedSelect',
  MULTISELECT = 'multiselect',
  JSONATA = 'jsonata',
  GRAPHQL_TYPE_SELECT = 'graphqlTypeSelect',
  GRAPHQL_QUERY = 'graphqlQuery',
  TEXTAREA = 'textarea',
  DATE = 'date',
  CHECKBOX_GROUP = 'checkboxGroup',
}

export enum DASHBOARD_TYPE {
  ORGANIZATION_DORA_SUMMARY = 'organization-dora-summary',
  ORGANIZATION_EP_SUMMARY = 'organization-ep-summary',
}

export enum DASHBOARD_PARAMETER_TYPE {
  CATEGORY = 'category',
  DATE = 'date/all-options',
  LOCATION = 'location/state',
  STRING = 'string/=',
}

export enum DORA_METRICS_CARD {
  AVERAGE_LEAD_TIME = 'Mean Lead Time for Changes',
  CHANGE_FAILURE_RATE = 'Change Failure Rate (Sev1/Sev2)',
  MEAN_TIME_TO_RESOLVE = 'Mean Time to Resolve (Sev1/Sev2)',
  WEEKLY_DEPLOYMENTS = 'Average Weekly Deployments Per Application',
}

export enum SURVEY_METRICS_CARD {
  AVERAGE_SURVEY_SCORE = 'Average Survey Score',
}

export enum TEAM_OWNERSHIP_TYPE {
  APPLICATION = 'application',
  REPOSITORY = 'repository',
  TASK_BOARD = 'task-board',
  PIPELINE = 'pipeline',
}

export enum ORGANIZATION_SOURCE {
  GITHUB = 'GitHub',
}

export enum SOURCE_KEY {
  GITHUB = 'github',
  GITHUB_ENTERPRISE = 'github-enterprise',
}

export enum METABASE_COLLECTION_TYPE {
  CARD = 'card',
  DASHBOARD = 'dashboard',
  PULSE = 'pulse',
}

export const NEXT_AUTH_ERROR_TYPE = {
  CREDENTIALS_SIGN_IN: 'CredentialsSignin',
} as const

export const NEXT_AUTH_PROVIDER_ID = {
  CREDENTIALS: 'credentials',
} as const

export enum NEXT_AUTH_PROVIDER_TYPE {
  OAUTH = 'oauth',
}

export enum DASHBOARD_NAME {
  ORGANIZATION_DORA_SUMMARY = 'Organization DORA Summary',
  ORGANIZATION_EP_SUMMARY = 'Organization Engineering Productivity Summary',
}

export const DASHBOARD_NAME_TO_DASHBOARD_TYPE: Record<
  DASHBOARD_NAME,
  DASHBOARD_TYPE
> = {
  [DASHBOARD_NAME.ORGANIZATION_DORA_SUMMARY]:
    DASHBOARD_TYPE.ORGANIZATION_DORA_SUMMARY,
  [DASHBOARD_NAME.ORGANIZATION_EP_SUMMARY]:
    DASHBOARD_TYPE.ORGANIZATION_EP_SUMMARY,
}

export const EVENT_KEY = {
  ENTER: 'Enter',
  SPACE: ' ',
  COMMA: ',',
  ARROW_UP: 'ArrowUp',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_DOWN: 'ArrowDown',
  ARROW_RIGHT: 'ArrowRight',
  ESCAPE: 'Escape',
} as const

export enum ADMIN_PORTAL_URL_HASH {
  PRIVACY = '#/admin-box/privacy',
  SSO = '#/admin-box/sso',
  SECURITY = '#/admin-box/security',
  AUDIT_LOGS = '#/admin-box/audits',
}

// Keys are used to identify modules in the code and in tenant preferences
export enum MODULE_KEY {
  ENGINEERING_PRODUCTIVITY = 'ENGINEERING_PRODUCTIVITY',
  SOFTWARE_QUALITY = 'SOFTWARE_QUALITY',
  INITIATIVES = 'INITIATIVES',
  SURVEY = 'SURVEY',
  AI_TRANSFORMATION = 'AI_TRANSFORMATION',
  RND_COST_CAPITALIZATION = 'RND_COST_CAPITALIZATION',
  CFO = 'CFO',
  DEVOPS_MANAGEMENT = 'DEVOPS_MANAGEMENT',
  SECURITY = 'SECURITY',
  TEAMS_INSIGHTS = 'TEAMS_INSIGHTS', // demo
  COMPLIANCE = 'COMPLIANCE', // demo
  FINOPS = 'FINOPS', // demo
}

export enum MODULE_NAME {
  ENGINEERING_PRODUCTIVITY = 'Engineering Productivity', // vcs_tms
  SOFTWARE_QUALITY = 'Software Quality', // qa
  INITIATIVES = 'Initiative Tracking',
  SURVEY = 'Developer Experience', // survey
  AI_TRANSFORMATION = 'Coding Assistants Impact',
  RND_COST_CAPITALIZATION = 'Software Capitalization',
  CFO = 'Investment Strategy',
  DEVOPS_MANAGEMENT = 'DevOps Management', // dora
  SECURITY = 'Security',
  TEAMS_INSIGHTS = 'Teams Insights', // demo
  COMPLIANCE = 'Compliance', // demo
  FINOPS = 'FinOps', // demo
}

// Aliases are defined in aion and used to generate dashboard paths
export const MODULE_ALIAS = {
  [MODULE_KEY.DEVOPS_MANAGEMENT]: 'OrganizationDoraSummary',
  [MODULE_KEY.ENGINEERING_PRODUCTIVITY]:
    'OrganizationEngineeringProductivitySummary',
  [MODULE_KEY.SOFTWARE_QUALITY]: 'OrganizationQASummary',
  [MODULE_KEY.INITIATIVES]: 'InitiativeTrackingSummary',
  [MODULE_KEY.SURVEY]: 'DeveloperExperienceSummary',
  [MODULE_KEY.AI_TRANSFORMATION]: 'AICopilotEvaluationSummary',
  [MODULE_KEY.RND_COST_CAPITALIZATION]: 'SoftwareCapitalizationSummary',
  [MODULE_KEY.CFO]: 'InvestmentStrategyImpactSummary',
  [MODULE_KEY.SECURITY]: 'Security',
  [MODULE_KEY.TEAMS_INSIGHTS]: 'OnboardingSummaryMetrics',
  [MODULE_KEY.COMPLIANCE]: 'ComplianceSummary',
  [MODULE_KEY.FINOPS]: 'FinOpsSummary',
}

export enum TABLE_ACTIONS_MODE {
  BUTTON_GROUP,
  DROPDOWN_MENU,
}

// TODO rename to MUTATION_ORIGIN since revisions were used for v1 graphs
export enum GRAPH_REVISION_ORIGIN {
  OWNERSHIP_PAGE = 'ownership-page',
  SCORECARD_PAGE = 'scorecard-page',
  QUERY_DEFINED_METRICS_MODAL = 'query-defined-metrics-modal',
  TEAM_PAGE = 'team-page',
  DELETE_EMPLOYEE_MODAL = 'delete-employee-modal',
  EMPLOYEE_PAGE = 'employee-page',
  EMPLOYEES_PAGE = 'employees-page',
  MERGE_EMPLOYEES_MODAL = 'merge-employees-modal',
  ASSIGN_CONNECTIONS_EMPLOYEES = 'assign-connections-employees',
  ASSIGN_CONNECTIONS_USERS = 'assign-connections-users',
  DATA_CLEANUP_PAGE = 'data-cleanup-page',
  INITIATIVES_PAGE = 'initiatives-page',
  RND_CAPITALIZATION_PAGE = 'rnd-capitalization-page',
}

export enum GITHUB_TOKEN_SCOPE {
  REPO = 'repo',
  READ_USER = 'read:user',
  USER = 'user',
  READ_ORG = 'read:org',
  ADMIN_ORG = 'admin:org',
}

export enum USER_TYPE {
  OUTSIDE_OF_ORG = 'OutsideOfOrg',
  OUTSIDE_OF_TEAM = 'OutsideOfTeam',
  IC = 'IC',
  MANAGER = 'Manager',
  UNKNOWN = 'Unknown',
}

export enum GITHUB_AUTH_TYPE {
  APP = 'githubApp',
  TOKEN = 'githubToken',
}

export const COLLECTION_NAME = {
  ENGINEERING_PRODUCTIVITY: 'Engineering Productivity',
  SOFTWARE_QUALITY: 'Software Quality',
  INITIATIVES: 'Initiative Tracking',
  SURVEY: 'Developer Experience',
  AI_TRANSFORMATION: 'Coding Assistants Impact',
  AI_TRANSFORMATION_OLD: 'AI Copilot Evaluation', // TODO remove after old version is deleted
  RND_COST_CAPITALIZATION: 'Software Capitalization',
  CFO: 'Investment Strategy',
  DEVOPS_MANAGEMENT: 'DevOps Management',
  SECURITY: 'Security',
} as const

export type CollectionName = ValuesType<typeof COLLECTION_NAME>

// Source: https://github.com/faros-ai/canonical-models/blob/cb3fda7f900a856c1332478f40d56d1ddb146113/resources/models/cicd.gql#L175
export enum ENVIRONMENT_CATEGORY {
  CANARY = 'Canary',
  CUSTOM = 'Custom',
  DEV = 'Dev',
  PROD = 'Prod',
  QA = 'QA',
  SANDBOX = 'Sandbox',
  STAGING = 'Staging',
}

export enum ACCOUNT_SYNC_MODE {
  INCREMENTAL = 'incremental',
  CLEAN_INCREMENTAL = 'clean_incremental',
  FULL = 'full',
}
